import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, Clock, ChevronRight, Tag, Flame } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/common/BottomNav';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { kioskService, productService } from '../services/api';
import { toast } from 'sonner';

// PromoScroll — carrossel horizontal com scroll contínuo automático (direita → esquerda)
// Implementado 2026-06-29: substituí requestAnimationFrame por CSS animation
// (translateX 0 → -50%) sobre lista duplicada [items, items] para loop infinito.
// Pausa ao passar mouse/touch. Velocidade ~50px/seg.
const PromoScroll = ({ products, kiosks, onKioskClick }) => {
  const scrollRef = useRef(null);

  // Find kiosk name for a product
  const getKioskName = (clientId) => {
    const k = kiosks.find(k => k.id === clientId);
    return k ? k.display_name : '';
  };

  // Configuração da animação CSS de scroll contínuo
  // 2026-06-29: Usa CSS @keyframes promoScroll (translateX 0 → -50%) ao invés de
  // requestAnimationFrame — mais performático e suave. Duração = scrollWidth/2 / 50px/s
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const totalWidth = el.scrollWidth / 2;
    const duration = totalWidth / 50; // ~50px/seg — velocidade suave mas visível
    el.style.animation = `promoScroll ${duration}s linear infinite`;
    el.animationPlayState = 'running';

    // Injeta @keyframes no <head> se ainda não existe
    if (!document.getElementById('promo-scroll-style')) {
      const style = document.createElement('style');
      style.id = 'promo-scroll-style';
      style.textContent = `
        @keyframes promoScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `;
      document.head.appendChild(style);
    }

    // Pause on hover/touch
    const onMouseEnter = () => { el.style.animationPlayState = 'paused'; };
    const onMouseLeave = () => { el.style.animationPlayState = 'running'; };

    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('touchstart', onMouseEnter, { passive: true });
    el.addEventListener('touchend', onMouseLeave, { passive: true });

    return () => {
      el.removeEventListener('mouseenter', onMouseEnter);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('touchstart', onMouseEnter);
      el.removeEventListener('touchend', onMouseLeave);
      el.style.animation = '';
    };
  }, []);

  // Double the items to create seamless loop
  const doubledProducts = [...products, ...products];

  return (
    <div
      ref={scrollRef}
      className="flex gap-3 overflow-hidden pb-2 -mx-4 px-4 scrollbar-hide"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', paddingLeft: '16px', paddingRight: '16px' }}
    >
      {doubledProducts.map((product, idx) => {
        // Calculate original_price: use API value or estimate 20% markup
        const originalPrice = product.original_price || (product.price * 1.2);
        const discount = Math.round(100 * (1 - product.price / originalPrice));

        return (
          <button
            key={`promo-${product.id}-${idx}`}
            // 2026-06-29: adicionado onClick para navegar ao quiosque do produto
            // Antes: card era estático, não clicável
            onClick={() => onKioskClick(product.client_id)}
            className="flex-shrink-0 w-64 bg-white rounded-xl shadow-md border border-border overflow-hidden active:scale-[0.97] transition-transform text-left"
          >
            {/* Image or gradient fallback */}
            {product.image_url ? (
              <div className="relative w-full h-28 overflow-hidden">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                {discount > 0 && (
                  <span className="absolute top-2 right-2 bg-accent text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                    -{discount}%
                  </span>
                )}
              </div>
            ) : (
              <div className="w-full h-28 bg-gradient-to-br from-accent/30 to-primary/20 flex items-center justify-center">
                <span className="text-3xl">🏖️</span>
              </div>
            )}
            {/* Card body */}
            <div className="p-3">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-sm text-foreground line-clamp-1">
                  {product.name}
                </h3>
                <span className="bg-accent/20 text-accent text-[10px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">
                  PROMO
                </span>
              </div>
              {getKioskName(product.client_id) && (
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {getKioskName(product.client_id)}
                </p>
              )}
              <div className="flex items-baseline gap-2 mt-1.5">
                <span className="font-bold text-base text-primary">
                  R$ {product.price.toFixed(2)}
                </span>
                {originalPrice > product.price && (
                  <span className="text-xs text-muted-foreground line-through">
                    R$ {originalPrice.toFixed(2)}
                  </span>
                )}
              </div>
              {product.description && (
                <p className="text-[10px] text-accent font-semibold mt-1 line-clamp-1">
                  {product.description}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { location, requestLocation, hasLocation } = useLocation();
  const [kiosks, setKiosks] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [promotionalProducts, setPromotionalProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Role-based redirect: if user is partner or admin, send them to their dashboard
  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (['kiosk_owner', 'kiosk_staff'].includes(user.role)) {
        navigate('/partner/dashboard', { replace: true });
      } else if (user.role === 'city_hall') {
        navigate('/cityhall/dashboard', { replace: true });
      } else if (user.role === 'brand') {
        navigate('/brand/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  // 2026-06-29: envolvido com useCallback pra satisfazer ESLint react-hooks/exhaustive-deps
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load nearby kiosks if location available
      let kiosksData = [];
      if (location) {
        kiosksData = await kioskService.getNearby(
          location.latitude,
          location.longitude
        );
      } else {
        kiosksData = await kioskService.list(null, true);
      }

      // Filter: only show open establishments on home
      const openKiosks = kiosksData.filter(k => k.effective_is_open !== false);
      setKiosks(openKiosks.slice(0, 5));

      // Load featured products
      const productsData = await productService.list(null, null, true);
      const featured = productsData.filter((p) => p.is_featured).slice(0, 6);
      setFeaturedProducts(featured);

      // Load promotional products for carousel
      try {
        let promoData = [];
        if (location) {
          promoData = await productService.listPromotional(location.latitude, location.longitude);
        } else {
          promoData = await productService.listPromotional(null, null);
        }
        // If API returns empty, fallback: filter from full product list
        if (!promoData || promoData.length === 0) {
          promoData = productsData.filter((p) => p.is_promotional || (p.original_price && p.original_price > p.price));
        }
        setPromotionalProducts(promoData.slice(0, 10));
      } catch (promoErr) {
        // non-fatal: promotional section just won't show
        setPromotionalProducts([]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  // 2026-06-29: location é dependência — recarrega quando muda
  }, [location]);

  // 2026-06-29: loadData agora é useCallback — pode ir no dep array
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEnableLocation = () => {
    requestLocation();
    toast.success('Localização ativada!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" text={t('common.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <ErrorMessage message={error} onRetry={loadData} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary to-secondary p-6 rounded-b-3xl shadow-lg">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-white">
                {t('home.greeting')}, {user?.full_name?.split(' ')[0] || 'Visitante'}! 🌊
              </h1>
              <p className="text-white/90 text-sm mt-1">
                {t('home.subtitle')}
              </p>
            </div>
            <img
              src="https://static.prod-images.emergentagent.com/jobs/38c5717c-99f2-4711-b6d9-c7c1cc9517e3/images/f02674562dcbb7e33a9e01d5c7f91e1404b16b69f9b73b7fbe740c041c7b44e6.png"
              alt="Beach Pay"
              className="w-12 h-12"
            />
          </div>

          {!hasLocation && (
            <button
              data-testid="enable-location-button"
              onClick={handleEnableLocation}
              className="w-full bg-white/20 backdrop-blur-sm border border-white/30 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/30 transition-colors active:scale-95"
            >
              <MapPin className="w-5 h-5" />
              {t('home.enableLocation')}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 mt-6 space-y-6">
        {/* Banner */}
        <div className="relative rounded-2xl overflow-hidden shadow-md">
          <img
            src="https://images.unsplash.com/photo-1536869338989-e7ffd2297454"
            alt="Praia"
            className="w-full h-40 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
            <div>
              <h3 className="font-['Outfit'] text-white text-lg font-bold">
                {t('home.summer')}
              </h3>
              <p className="text-white/90 text-sm">{t('home.summerSub')}</p>
            </div>
          </div>
        </div>

        {/* Promoções Carousel - scroll horizontal contínuo */}
        {promotionalProducts.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-['Outfit'] text-xl font-bold text-foreground">
                🔥 {t('home.promotions', 'Promoções')}
              </h2>
              <span className="text-xs text-muted-foreground">
                Ofertas perto de você
              </span>
            </div>
            <PromoScroll
              products={promotionalProducts}
              kiosks={kiosks}
              onKioskClick={(id) => navigate(`/kiosk/${id}`)}
            />
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-2">
          <button
            data-testid="qa-map"
            onClick={() => navigate('/map')}
            className="bg-white rounded-xl p-3 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex flex-col items-center"
          >
            <MapPin className="w-6 h-6 text-secondary mb-1" strokeWidth={2.5} />
            <p className="text-xs font-semibold">{t('quickActions.map')}</p>
          </button>

          <button
            data-testid="qa-coupons"
            onClick={() => navigate('/coupons')}
            className="bg-white rounded-xl p-3 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex flex-col items-center"
          >
            <span className="text-2xl mb-1">🎟️</span>
            <p className="text-xs font-semibold">{t('quickActions.coupons')}</p>
          </button>

          <button
            data-testid="qa-cashback"
            onClick={() => navigate('/cashback')}
            className="bg-white rounded-xl p-3 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex flex-col items-center"
          >
            <span className="text-2xl mb-1">💰</span>
            <p className="text-xs font-semibold">{t('quickActions.cashback')}</p>
          </button>

          <button
            data-testid="qa-info"
            onClick={() => navigate('/beach-info')}
            className="bg-white rounded-xl p-3 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex flex-col items-center"
          >
            <span className="text-2xl mb-1">📰</span>
            <p className="text-xs font-semibold">{t('quickActions.beach')}</p>
          </button>
        </div>

        {/* Referral Banner */}
        <button
          data-testid="referral-banner"
          onClick={() => navigate('/referral')}
          className="w-full bg-gradient-to-r from-accent to-primary rounded-2xl p-4 text-white shadow-lg flex items-center gap-3 active:scale-[0.98] transition-transform"
        >
          <div className="text-3xl">🎁</div>
          <div className="flex-1 text-left">
            <p className="font-['Outfit'] font-bold text-lg">{t('home.referralTitle')}</p>
            <p className="text-xs text-white/90">{t('home.referralSub')}</p>
          </div>
          <span className="text-white/80 text-xs">→</span>
        </button>

        {/* Quick Actions Row 2 */}
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="qa-loyalty"
            onClick={() => navigate('/loyalty')}
            className="bg-white rounded-xl p-4 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex items-center gap-2"
          >
            <span className="text-2xl">⭐</span>
            <div className="text-left">
              <p className="text-sm font-semibold">{t('home.loyaltyTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('home.loyaltySub')}</p>
            </div>
          </button>
          <button
            data-testid="qa-orders"
            onClick={() => navigate('/orders')}
            className="bg-white rounded-xl p-4 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex items-center gap-2"
          >
            <Clock className="w-7 h-7 text-primary" strokeWidth={2.5} />
            <div className="text-left">
              <p className="text-sm font-semibold">{t('home.ordersTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('home.ordersSub')}</p>
            </div>
          </button>
        </div>

        {/* Nearby Kiosks */}
        {kiosks.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-['Outfit'] text-xl font-bold text-foreground">
                {hasLocation ? t('home.nearby') : t('home.available')}
              </h2>
              <button
                onClick={() => navigate('/map')}
                className="text-primary text-sm font-semibold flex items-center gap-1"
              >
                {t('home.viewAll')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {kiosks.map((kiosk) => (
                <button
                  key={kiosk.id}
                  data-testid={`kiosk-card-${kiosk.id}`}
                  onClick={() => navigate(`/kiosk/${kiosk.id}`)}
                  className="w-full bg-white rounded-xl p-4 shadow-md border border-border hover:shadow-lg transition-shadow text-left active:scale-[0.98]"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-16 h-16 rounded-lg flex-shrink-0 overflow-hidden">
                      <img
                        src={kiosk.cover_image || '/images/promenade/kiosk-promenade.jpg'}
                        alt={kiosk.display_name}
                        aria-hidden="true"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">
                        {kiosk.display_name}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {kiosk.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="inline-flex items-center gap-1 text-xs text-secondary">
                          <MapPin className="w-3 h-3" />
                          {/* 2026-06-29: trocado de estabelecimentoAvailable/estabelecimentoNearYou
                              para kioskAvailable/kioskNearYou — chaves que existem no pt.json */}
                          {hasLocation ? t('home.kioskNearYou') : t('home.kioskAvailable')}
                        </span>
                        {/* 2026-06-29: usa effective_is_open (manual + horário) ao invés de is_open */}
                        <span className={`inline-flex items-center gap-1 text-xs ${kiosk.effective_is_open ? 'text-green-600' : 'text-red-500'}`}>
                          {kiosk.effective_is_open ? (
                            <>
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              {t('kiosk.open')}
                            </>
                          ) : (
                            <>
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              {t('kiosk.closed')}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Featured Products */}
        {featuredProducts.length > 0 && (
          <div>
            <h2 className="font-['Outfit'] text-xl font-bold text-foreground mb-3">
              {t('home.featured')}
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  // 2026-06-29: adicionado onClick + cursor-pointer — antes era card estático não-clicável
                  onClick={() => navigate(`/kiosk/${product.client_id}`)}
                  className="bg-white rounded-xl shadow-md border border-border overflow-hidden cursor-pointer active:scale-[0.97] transition-transform"
                >
                  {product.image_url && (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-32 object-cover"
                    />
                  )}
                  <div className="p-3">
                    <h3 className="font-semibold text-sm text-foreground line-clamp-1">
                      {product.name}
                    </h3>
                    <p className="text-primary font-bold mt-1">
                      R$ {product.price.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Home;
